"use client";
import React, { useState, useEffect } from 'react';
import Loader from '../Loader/Loader';
import { clientLogError, clientLogInfo } from '@/app/helpers/clientLoggingHelper';
import Accordion from '../Accordion/Accordion';
import { GetSizeGroupsShortNames } from '@/app/helpers/clientUtilityHelper';
import styles from './sizebyareav2.module.scss';
import { iSizeArea } from '@/app/models/componentModels/sizeArea';
import StorageSizeCardAdvanced from '../StorageSizeCardAdvanced/StorageSizeCardAdvanced';
import { iGetAllSitesByRegionAndSizeGroup } from '@/app/models/apiModels/requestModels/getAllSitesByRegionAndSizeGroupRequestModel';
import { iSizeConfigurations } from '@/app/models/configurations';

interface Props {
    country: string;
    // sizeCodeId: string;
    groupName: string;
    translations: any;
    // countdownTimer: number;
    orderStep1Url: string;
    sizeGroupsMap: Map<string, { shortName: string; fullName: string }>;
    temporaryOrgName?: string;
    useCustomStyling?: boolean;
    sizeConfigurations: iSizeConfigurations;
    culture: string;
}


export default function SizeByAreaV2({ country, groupName, translations, orderStep1Url, sizeGroupsMap, temporaryOrgName, useCustomStyling, sizeConfigurations, culture }: Props) {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isLoadingFacilities, setIsLoadingFacilities] = useState<boolean>(true);
    const [areas, setAreas] = useState<iSizeArea[]>([]);//
    const [sizeGroups, setSizeGroups] = useState<Map<string, boolean>>(new Map());
    const [activeGroupSize, setActiveGroupSize] = useState<string | null>(null);

    useEffect(() => {
        // Fetch areas useEffect
        const fetchAreas = async () => {
            try {
                clientLogInfo("SizeByAreaV2.tsx", "fetchAreas", `Country: ${country}`);
                const requestModel: iGetAllSitesByRegionAndSizeGroup = {
                    orderStep1Url: orderStep1Url,
                    movingBoxes: sizeConfigurations.movingBoxes,
                    culture: culture
                };

                const response = await fetch('/api/site/getAllSitesByRegionAndSizeGroup', {
                    method: 'POST',
                    headers: {
                        'Country': country,
                    },
                    body: JSON.stringify(requestModel),
                });

                if (!response.ok) {
                    clientLogError("SizeByAreaV2.tsx", "fetchAreas", `Network response was not ok: ${JSON.stringify(response)}`);
                    throw new Error(`Network response was not ok ${JSON.stringify(response)}`);
                }

                clientLogInfo("SizeByAreaV2.tsx", "fetchAreas", `Response ok: ${JSON.stringify(response)}`);
                const responseJson = await response.json();
                const fetchedAreas = responseJson as iSizeArea[];

                setAreas(fetchedAreas);
            } catch (error) {
                clientLogError("SizeByArea.tsx", "fetchAreas", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchAreas();
    }, []);

    useEffect(() => {
        if (!areas || areas.length === 0) {
            return;
        }

        const existingSizeGroups = GetSizeGroupsShortNames();
        const updatedSizeGroups = new Map<string, boolean>();

        existingSizeGroups.forEach((groupShortName) => {
            const exists = areas.some((area) =>
                area.sizeGroups.some((sizeGroup) =>
                    sizeGroup.groupNameShort === groupShortName && sizeGroup.sizeCards.length > 0
                )
            )

            updatedSizeGroups.set(groupShortName, exists);
        });

        existingSizeGroups.push("All");
        updatedSizeGroups.set("All", true);

        setSizeGroups(updatedSizeGroups);
    }, [areas]);

    useEffect(() => {
        if (groupName) {
            const sizeGroupsArray = Array.from(sizeGroupsMap.values());

            for (const entry of sizeGroupsArray) {
                if (entry.fullName === groupName) {
                    setActiveGroupSize(entry.shortName);
                    return;
                }
            }
        } else {
            const firstAvailableGroupSize = getFirstTrueSizeGroup();
            const defaultGroupName = firstAvailableGroupSize ? firstAvailableGroupSize : null;

            if (defaultGroupName === null) {
                return;
            }
            setActiveGroupSize(defaultGroupName);
        }
    }, [sizeGroups]);

    const getShortNameFromSizeGroup = (fullName: string): string | undefined => {
        const entry = Array.from(sizeGroupsMap.entries()).find(([key, value]) => value.fullName === fullName);
        return entry && entry[1] ? entry[1].shortName : undefined;
    }

    const getGroupNameFromShortName = (shortName: string): string | undefined => {
        if (shortName === "All") {
            return translations.general.all;
        }

        const entry = Array.from(sizeGroupsMap.entries()).find(([key, value]) => value.shortName === shortName);
        return entry && entry[1] ? entry[1].fullName : undefined;
    }

    const activeButtonClass = function (group: [string, boolean]) {
        const isActive = group[0] === activeGroupSize;

        let classes = `${styles.filterButton} flex-grow ${isActive ? `${styles.isActive} bg-gs-green` : ""}`// "btn flex-grow";

        return classes;
    }

    const handleChangeSizeGroupClick = (group: [string, boolean]) => {
        setIsLoadingFacilities(true);
        setActiveGroupSize(group[0]);
    }

    useEffect(() => {
        setIsLoadingFacilities(false);
    }, [activeGroupSize]);

    const getFirstTrueSizeGroup = (): string | undefined => {
        const entry = Array.from(sizeGroups.entries()).find(([_, value]) => value === true);
        return entry ? entry[0] : undefined;
    };

    return (
        <div className={`areaContainer grid grid-cols-1 gap-6`}>
            {isLoading ? (
                <Loader loading={isLoading} useFullWindow={false} useNoBackground={true} />
            ) : (
                <>
                    <div>
                        <div className={`${styles.sizeGroupsContainerDesktop} hidden lg:flex flex-wrap w-full justify-start`}>
                            {sizeGroups && Array.from(sizeGroups.entries()).map((group, index) => (
                                <button
                                    key={index}
                                    className={`${activeButtonClass(group)}`}
                                    disabled={!group[1]}
                                    onClick={
                                        () => { group[1] ? handleChangeSizeGroupClick(group) : undefined; } // Only call the function if the button is enabled
                                    }
                                >
                                    {getGroupNameFromShortName(group[0])}
                                </button>
                            ))}
                        </div>
                        <div className={`${styles.sizeGroupsContainerSmall} lg:hidden grid grid-cols-1 w-full`}>
                            {sizeGroups && Array.from(sizeGroups.entries()).length > 0 ? (
                                <>
                                    {Array.from(sizeGroups.entries()).reduce<JSX.Element[][]>((rows, group, index) => {
                                        const rowIndex = Math.floor(index / 4); // Determine the row index

                                        if (!rows[rowIndex]) rows[rowIndex] = []; // Initialize the row if it doesn't exist

                                        rows[rowIndex].push(
                                            <button
                                                key={index}
                                                disabled={!group[1]}
                                                className={`${activeButtonClass(group)}`}
                                                onClick={() => handleChangeSizeGroupClick(group)}
                                            >
                                                {group[0]}
                                            </button>
                                        );
                                        return rows;
                                    }, []).map((row, rowIndex) => (
                                        <div key={rowIndex} className="row-container flex justify-start">
                                            {row}
                                        </div>
                                    ))
                                    }
                                </>
                            ) : (
                                <span>{translations.blocks.storageSizesAdvanced.noSizesFound}</span>
                            )}
                        </div >
                    </div >
                    {areas && areas.map((area, index) => (
                        <React.Fragment key={index}>
                            {(area.sizeGroups?.find((sizeGroup) => sizeGroup.groupNameShort === activeGroupSize)?.sizeCards?.length ?? 0) > 0 && (
                                <Accordion
                                    title={area.name}
                                    translations={translations}
                                    
                                    className={`rounded-[16px] ${styles.facilityAccordion}`}
                                >
                                    <div className="mb-4">
                                        {area.description}
                                    </div>
                                    {isLoadingFacilities ? (
                                        <Loader loading={isLoadingFacilities} useFullWindow={false} useNoBackground={true} />
                                    ) : (
                                        <>
                                            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                                                {!isLoadingFacilities && area.sizeGroups && area.sizeGroups.find((sizeGroup) => sizeGroup.groupNameShort === activeGroupSize)?.sizeCards?.map((sizeCard, index) => {
                                                    return (
                                                        <StorageSizeCardAdvanced
                                                            storage={sizeCard}
                                                            country={country}
                                                            translations={translations}
                                                            isOnSizeByAreaPage={true}
                                                            temporaryOrgName={temporaryOrgName}
                                                            useCustomStyling={useCustomStyling}
                                                            key={index}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </Accordion>
                            )}
                        </React.Fragment>
                    ))}
                </>
            )
            }
        </div >
    );
}



